import React, { useEffect, useState, useRef } from 'react';

interface GoogleCallbackProps {
  onLogin: (user: any) => void;
}

const GoogleCallback: React.FC<GoogleCallbackProps> = ({ onLogin }) => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processando autenticação...');
  const [showError, setShowError] = useState(false);
  const processedRef = useRef(false);

  const validateAuthParams = (idToken: string | null, error: string | null, state: string | null) => {
    const savedState = sessionStorage.getItem('google_auth_state');
    
    if (error) {
      setStatus('error');
      setMessage(`Erro na autenticação: ${error}`);
      setShowError(true);
      return false;
    }
    
    if (!idToken) {
      setStatus('error');
      setMessage('Token não recebido do Google');
      setTimeout(() => setShowError(true), 2000);
      return false;
    }
    
    if (state && savedState && state !== savedState) {
      setStatus('error');
      setMessage('Estado de segurança inválido');
      setShowError(true);
      return false;
    }
    
    return true;
  };

  const handleAuthSuccess = (result: any) => {
    setStatus('success');
    setMessage('Login realizado com sucesso!');
    
    sessionStorage.removeItem('google_auth_state');
    localStorage.setItem('token', result.token);
    
    if (result.needsPhoneCompletion) {
      setMessage('Redirecionando para completar cadastro...');
      setTimeout(() => {
        window.location.href = `/complete-profile?token=${result.token}`;
      }, 1000);
    } else {
      onLogin(result.usuario);
      setMessage('Redirecionando para dashboard...');
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  };

  const handleAuthError = (message: string) => {
    setStatus('error');
    setMessage(message);
    setTimeout(() => setShowError(true), 1000);
  };

  useEffect(() => {
    if (processedRef.current) {
      return;
    }

    const processCallback = async () => {
      try {
        processedRef.current = true;
        
        const urlParams = new URLSearchParams(window.location.search);
        const fragment = new URLSearchParams(window.location.hash.substring(1));
        
        const idToken = fragment.get('id_token') ?? urlParams.get('id_token');
        const error = urlParams.get('error') ?? fragment.get('error');
        const state = urlParams.get('state') ?? fragment.get('state');
        
        if (!validateAuthParams(idToken, error, state)) {
          return;
        }
        
        setMessage('Validando com servidor...');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: idToken,
            tipo: 'paciente'
          }),
        });

        const result = await response.json();
        
        if (result.success) {
          handleAuthSuccess(result);
        } else {
          handleAuthError(result.message ?? 'Falha na validação do token');
        }
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
        handleAuthError(`Erro interno: ${errorMessage}`);
      }
    };

    processCallback();
  }, [onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">🔄 Processando Login</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-green-800 mb-2">🎉 Login Realizado!</h2>
            <p className="text-green-600 mb-2">{message}</p>
            <p className="text-sm text-gray-500">Redirecionando...</p>
          </>
        )}
        
        {status === 'error' && showError && (
          <>
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">⚠️ Erro no Login</h2>
            <p className="text-red-600 mb-4">{message}</p>
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              onClick={() => window.location.href = '/'}
            >
              Tentar Novamente
            </button>
          </>
        )}
        
        {status === 'error' && !showError && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">🔄 Processando Login</h2>
            <p className="text-gray-600">Validando autenticação...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
